'use client';

import { useMemo } from 'react';
import { Box, VStack } from '@terra/ui-styled-system';
import { Text } from '../../../../../components/styled/text';
import { MarkdownPreview } from '../../../../markdown-preview';
import {
  normalizeHttpHeaders,
  resolveEffectiveHttpVersion,
} from '@terra/shared-utils';
import type { OnOpenChatObject } from '../../hitl-types';
import { formatResponseAsHttp, maskSensitiveValue } from './HttpRenderer.utils';

type HttpRendererProps = {
  data: unknown;
  maxHeight?: string;
  onOpenObject?: OnOpenChatObject;
};

const REQUEST_LABEL = 'Request';
const RESPONSE_LABEL = 'Response';

type ParsedHttpData = {
  requestId?: string;
  request?: {
    method?: string;
    url?: string;
    headers?: Record<string, string>;
    body?: string;
  };
  response?: {
    statusCode?: number;
    statusText?: string;
    headers?: Record<string, string>;
    body?: string;
  };
};

function getStringProperty(
  obj: Record<string, unknown>,
  key: string,
): string | undefined {
  return typeof obj[key] === 'string' ? obj[key] : undefined;
}

function parseHttpData(data: unknown): ParsedHttpData {
  let obj: Record<string, unknown>;

  if (typeof data === 'string') {
    try {
      obj = JSON.parse(data) as Record<string, unknown>;
    } catch {
      return {};
    }
  } else if (typeof data === 'object' && data !== null) {
    obj = data as Record<string, unknown>;
  } else {
    return {};
  }

  const parsed: ParsedHttpData = {
    requestId:
      getStringProperty(obj, 'requestId') ??
      getStringProperty(obj, 'id') ??
      (typeof obj.request === 'object' && obj.request !== null
        ? getStringProperty(obj.request as Record<string, unknown>, 'id')
        : undefined),
  };

  if (typeof obj.request === 'object' || typeof obj.response === 'object') {
    return {
      ...parsed,
      ...(obj as ParsedHttpData),
    };
  }

  if (typeof obj.url === 'string' || typeof obj.method === 'string') {
    const options =
      obj.options && typeof obj.options === 'object'
        ? (obj.options as Record<string, unknown>)
        : undefined;

    const baseUrl = typeof obj.url === 'string' ? obj.url : undefined;

    const rawQueryParams =
      (options &&
        (options.queryParams || options.params || options.searchParams)) ||
      (obj as Record<string, unknown>).queryParams ||
      (obj as Record<string, unknown>).params ||
      (obj as Record<string, unknown>).searchParams;

    let resolvedUrl = baseUrl;
    const isQueryParamsObject =
      rawQueryParams &&
      typeof rawQueryParams === 'object' &&
      !Array.isArray(rawQueryParams);

    if (baseUrl && isQueryParamsObject) {
      try {
        const url = new URL(baseUrl);

        Object.entries(rawQueryParams as Record<string, unknown>).forEach(
          ([key, value]) => {
            url.searchParams.append(key, String(value));
          },
        );
        resolvedUrl = url.toString();
      } catch {
        resolvedUrl = baseUrl;
      }
    }

    parsed.request = {
      method:
        typeof obj.method === 'string'
          ? obj.method
          : typeof options?.method === 'string'
            ? options.method
            : undefined,
      url: resolvedUrl,
      headers:
        options?.headers && typeof options.headers === 'object'
          ? (options.headers as Record<string, string>)
          : obj.headers && typeof obj.headers === 'object'
            ? (obj.headers as Record<string, string>)
            : undefined,
      body:
        typeof options?.body === 'string'
          ? options.body
          : typeof options?.body === 'object'
            ? JSON.stringify(options.body, null, 2)
            : typeof options?.formData === 'string'
              ? options.formData
              : typeof options?.formData === 'object'
                ? JSON.stringify(options.formData, null, 2)
                : typeof obj.body === 'string'
                  ? obj.body
                  : typeof obj.body === 'object'
                    ? JSON.stringify(obj.body, null, 2)
                    : typeof obj.formData === 'string'
                      ? obj.formData
                      : typeof obj.formData === 'object'
                        ? JSON.stringify(obj.formData, null, 2)
                        : undefined,
    };
  }

  if (typeof obj.statusCode === 'number' || typeof obj.status === 'number') {
    let responseBody: string | undefined;

    if (typeof obj.responseBody === 'string') {
      responseBody = obj.responseBody;
    } else if (typeof obj.body === 'string') {
      responseBody = obj.body;
    } else if (typeof obj.body === 'object' && obj.body !== null) {
      responseBody = JSON.stringify(obj.body, null, 2);
    } else if (typeof obj.data === 'string') {
      responseBody = obj.data;
    } else if (typeof obj.data === 'object' && obj.data !== null) {
      responseBody = JSON.stringify(obj.data, null, 2);
    } else if (typeof obj.content === 'string') {
      responseBody = obj.content;
    } else if (typeof obj.content === 'object' && obj.content !== null) {
      responseBody = JSON.stringify(obj.content, null, 2);
    }

    parsed.response = {
      statusCode:
        typeof obj.statusCode === 'number'
          ? obj.statusCode
          : typeof obj.status === 'number'
            ? obj.status
            : undefined,
      statusText:
        typeof obj.statusText === 'string' ? obj.statusText : undefined,
      headers:
        obj.responseHeaders && typeof obj.responseHeaders === 'object'
          ? (obj.responseHeaders as Record<string, string>)
          : obj.headers && typeof obj.headers === 'object'
            ? (obj.headers as Record<string, string>)
            : undefined,
      body: responseBody,
    };
  }

  return parsed;
}

// Format request as HTTP markdown code block
function formatRequestAsHttp(request: ParsedHttpData['request']): string {
  if (!request?.url) return '';

  let displayPath = '/';
  let host = '';

  try {
    const url = new URL(request.url);

    displayPath = url.pathname + url.search;
    host = url.host;
  } catch {
    displayPath = request.url;
  }

  const method = request.method || 'GET';
  const mergedHeaders = host
    ? normalizeHttpHeaders({ Host: host, ...(request.headers || {}) })
    : normalizeHttpHeaders(request.headers || {});
  const headerLines = Object.entries(mergedHeaders).map(([key, value]) => {
    const headerValue = Array.isArray(value) ? value.join(', ') : value;

    return `${key}: ${maskSensitiveValue(key, headerValue)}`;
  });

  const httpVersion = resolveEffectiveHttpVersion({
    headers: request.headers,
  });

  let content = `${method.toUpperCase()} ${displayPath} ${httpVersion}`;

  if (headerLines.length > 0) {
    content += `\n${headerLines.join('\n')}`;
  }

  if (request.body) {
    content += `\n\n${request.body}`;
  }

  return `\`\`\`http\n${content}\n\`\`\``;
}

export function HttpRenderer({
  data,
  maxHeight = '400px',
  onOpenObject,
}: HttpRendererProps) {
  const parsedData = useMemo(() => parseHttpData(data), [data]);

  const { requestId, request, response } = parsedData;

  if (!request && !response) {
    return null;
  }

  const requestMarkdown = request ? formatRequestAsHttp(request) : '';
  const responseMarkdown = response ? formatResponseAsHttp(response) : '';
  const canOpenRequest = Boolean(requestId && onOpenObject);
  const handleOpenRequest = () => {
    if (!requestId || !onOpenObject) return;
    onOpenObject({
      type: 'request',
      id: requestId,
      label: request?.url,
    });
  };

  return (
    <VStack
      width="100%"
      maxHeight={maxHeight}
      overflowY="auto"
      gap={3}
      cursor={canOpenRequest ? 'pointer' : undefined}
      onClick={canOpenRequest ? handleOpenRequest : undefined}
    >
      {request && (
        <Box width="100%">
          <Box
            px={3}
            py={2}
            bg="bg.muted"
            borderBottomWidth="1px"
            borderColor="border.default"
            borderTopRadius="md"
          >
            <Text fontSize="xs" fontWeight="medium" color="fg.muted">
              {REQUEST_LABEL}
            </Text>
          </Box>
          <Box bg="bg.subtle" borderBottomRadius="md" overflow="hidden">
            <MarkdownPreview source={requestMarkdown} fontSize={12} compact />
          </Box>
        </Box>
      )}

      {response && (
        <Box width="100%">
          <Box
            px={3}
            py={2}
            bg="bg.muted"
            borderBottomWidth="1px"
            borderColor="border.default"
            borderTopRadius="md"
          >
            <Text fontSize="xs" fontWeight="medium" color="fg.muted">
              {RESPONSE_LABEL}
            </Text>
          </Box>
          <Box bg="bg.subtle" borderBottomRadius="md" overflow="hidden">
            <MarkdownPreview source={responseMarkdown} fontSize={12} compact />
          </Box>
        </Box>
      )}
    </VStack>
  );
}
